import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { castList, episodes } from '@/mocks/siteData';
import EpisodeCard from '@/components/feature/EpisodeCard';
import CastCard from '@/components/feature/CastCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/feature/Pagination';
import SiteSidebar from '@/components/feature/SiteSidebar';

const PAGE_SIZE = 8;

export default function PlotCategory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get('q') || '').trim();

  const [sortDesc, setSortDesc] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    document.title = '善良的小姨子 - 剧集剧情介绍';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        'content',
        '详细介绍《善良的小姨子》各剧集剧情，帮助用户快速了解内容。'
      );
    }
  }, []);

  const matchedCast = useMemo(() => {
    if (!keyword) return [];
    const kw = keyword.toLowerCase();
    return castList.filter(
      (c) => c.name.toLowerCase().includes(kw) || c.role.toLowerCase().includes(kw)
    );
  }, [keyword]);

  const filtered = useMemo(() => {
    let list = [...episodes];
    if (keyword) {
      const kw = keyword.toLowerCase();
      const castEpisodeSet = new Set<number>();
      matchedCast.forEach((c) => c.episodes.forEach((e) => castEpisodeSet.add(e)));
      list = list.filter(
        (ep) =>
          ep.title.toLowerCase().includes(kw) ||
          ep.summary.toLowerCase().includes(kw) ||
          castEpisodeSet.has(ep.episode)
      );
    }
    list.sort((a, b) => (sortDesc ? b.episode - a.episode : a.episode - b.episode));
    return list;
  }, [keyword, matchedCast, sortDesc]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [keyword, sortDesc]);

  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetSearch = () => setSearchParams({});

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10 animate-fade-up">
      <Breadcrumb items={[{ label: '剧情介绍' }]} />

      <div className="mt-5 mb-8">
        <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl mb-3">
          善良的小姨子 - 剧集剧情介绍
        </h1>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
          按集数浏览《善良的小姨子》全部剧情，点击任意一集即可进入播放演示页查看该集详细介绍。
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <SiteSidebar />

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-background-200">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-list-check-2 w-5 h-5 flex items-center justify-center text-primary-600"></i>
              {keyword ? (
                <span>
                  关键词「<span className="text-accent-700 font-medium">{keyword}</span>」共找到{' '}
                  <span className="text-foreground-950 font-semibold">{filtered.length}</span> 集
                </span>
              ) : (
                <span>
                  共 <span className="text-foreground-950 font-semibold">{episodes.length}</span> 集
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序</span>
              <div className="inline-flex bg-background-100 border border-background-200 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setSortDesc(false)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    !sortDesc ? 'bg-primary-500 text-background-50' : 'text-foreground-700'
                  }`}
                >
                  集数升序
                </button>
                <button
                  type="button"
                  onClick={() => setSortDesc(true)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer ${
                    sortDesc ? 'bg-primary-500 text-background-50' : 'text-foreground-700'
                  }`}
                >
                  集数降序
                </button>
              </div>
            </div>
          </div>

          {keyword && matchedCast.length > 0 && (
            <div className="mb-6 bg-accent-50 border border-accent-200 rounded-lg p-5">
              <h2 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
                <i className="ri-user-star-line w-5 h-5 flex items-center justify-center text-accent-600"></i>
                匹配到的演员
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {matchedCast.map((member) => (
                  <CastCard key={member.id} member={member} />
                ))}
              </div>
            </div>
          )}

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {pageItems.map((ep) => (
                  <EpisodeCard key={ep.episode} episode={ep} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 px-6 text-center">
              <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-inbox-2-line text-3xl"></i>
              </span>
              <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-2">
                没有找到匹配的剧情
              </h2>
              <p className="text-sm text-foreground-500 mb-5">
                换个关键词试试，或返回查看全部剧集剧情介绍。
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {keyword && (
                  <button
                    type="button"
                    onClick={resetSearch}
                    className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    重置搜索
                  </button>
                )}
                <Link
                  to="/category/cast"
                  className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-accent-400 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  去演员列表看看
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}